﻿import { useEffect, useMemo, useRef, useState } from 'react'
import { Chart, registerables } from 'chart.js'
import { useApp } from '../context/AppContext'
import { sileo } from 'sileo'
import HeaderSection from '../components/Shared/HeaderSection'
import AuthAccessValidator from '../components/Auth/AuthAccessValidator'
import { employeeService } from '../services/employeeService'
import generateReportPDF from '../utils/GenerateReportPDF'
import { getSlaInfo, SLA_META } from '../utils/sla'
import '../styles/data-table.css'

Chart.register(...registerables)

// Colores de las situaciones (usados en gráficos y badges)
const SIT_COLORS = {
  Pendiente: '#f59e0b',
  'En curso': '#3b82f6',
  Atendido: '#22c55e',
}

/**
 * Deriva la situación de un ticket dentro del flujo de trabajo.
 * - Atendido:  el ticket está marcado como completado.
 * - En curso:  está en progreso (statusId === 2) y no completado.
 * - Pendiente: cualquier otro estado sin completar.
 *
 * @param {Object} t - Ticket.
 * @returns {'Atendido'|'En curso'|'Pendiente'}
 */
const getSituacion = (t) => {
  if (t.completed) return 'Atendido'
  if (Number(t.statusId) === 2) return 'En curso'
  return 'Pendiente'
}

// RGB base de cada columna para el heatmap del resumen por persona
const HEAT_RGB = {
  pendientes: '245, 158, 11', // ámbar
  enCurso: '59, 130, 246', // azul
  atendidos: '34, 197, 94', // verde
  total: '99, 102, 241', // índigo
  breached: '239, 68, 68', // rojo (SLA incumplido)
}

/**
 * Color para un porcentaje de cumplimiento de SLA.
 * @param {number|null} pct
 * @returns {string}
 */
const slaPctColor = (pct) => {
  if (pct === null || pct === undefined) return 'var(--gm-text-muted)'
  if (pct >= 90) return 'var(--gm-success)'
  // --accent es el ámbar de la marca: sirve de relleno, no de texto (2.3:1
  // sobre blanco). --accent-text es la versión legible, y en oscuro vale igual.
  if (pct >= 70) return 'var(--accent-text)'
  return 'var(--gm-danger)'
}

/** Luminancia relativa (WCAG) de un color dado como [r, g, b]. */
const luminancia = ([r, g, b]) => {
  const canal = (x) => {
    const v = x / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b)
}

/**
 * Devuelve el estilo de una celda del heatmap: fondo con intensidad proporcional
 * al valor respecto al máximo de la columna, y el texto en blanco o en negro
 * según lo claro que quede ese fondo.
 *
 * Antes el texto se ponía blanco cuando el valor superaba el 55 % del máximo,
 * sin mirar el color: en las columnas de color claro (el ámbar de «Pendiente»,
 * el verde de «Atendido») el fondo seguía siendo casi blanco y el número
 * desaparecía. Ahora se mezcla el color con la superficie y se decide por la
 * claridad del resultado, que es lo que de verdad ve el ojo.
 *
 * @param {number} value - Valor de la celda.
 * @param {number} max - Máximo de la columna.
 * @param {string} rgb - Color base "r, g, b".
 * @param {boolean} isDark - Si la app está en modo oscuro (cambia la superficie).
 * @returns {React.CSSProperties}
 */
const heatStyle = (value, max, rgb, isDark) => {
  if (!value) return {}
  const ratio = value / max
  const alpha = 0.12 + ratio * 0.73 // 0.12 → 0.85
  const base = rgb.split(',').map((n) => parseFloat(n))
  const superficie = isDark ? [19, 28, 46] : [255, 255, 255]
  const mezcla = base.map((c, i) => c * alpha + superficie[i] * (1 - alpha))

  // Se mide contra los dos candidatos y gana el que se lea mejor, en vez de
  // cortar por un umbral: en los colores de claridad media ningún umbral fijo
  // acierta siempre.
  const lFondo = luminancia(mezcla)
  const contra = (l) => (Math.max(l, lFondo) + 0.05) / (Math.min(l, lFondo) + 0.05)
  const TINTA = [17, 24, 39]
  const sobreTinta = contra(luminancia(TINTA))
  const sobreBlanco = contra(luminancia([255, 255, 255]))

  return {
    background: `rgba(${rgb}, ${alpha})`,
    color: sobreTinta >= sobreBlanco ? '#111827' : '#ffffff',
    fontWeight: 600,
    borderRadius: 6,
  }
}

/**
 * Página de generación de reportes de tickets agrupados por persona.
 *
 * Permite filtrar por rango de fechas, plataforma de origen, situación
 * (pendiente / en curso / atendido) y responsable; visualizar un resumen por
 * persona con KPIs y un detalle; y exportar el resultado a PDF o CSV.
 *
 * @component
 * @returns {JSX.Element}
 */
const Reports = () => {
  const { supabase } = useApp()

  const [tickets, setTickets] = useState([])
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)

  // Filtros del reporte
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [origin, setOrigin] = useState('all')
  const [situacion, setSituacion] = useState('all')
  const [personId, setPersonId] = useState('all')

  // Refs y tema para los gráficos
  const barRef = useRef(null)
  const doughnutRef = useRef(null)
  const [isDark, setIsDark] = useState(false)

  useEffect(() => {
    const read = () =>
      setIsDark(
        document.documentElement.getAttribute('data-bs-theme') === 'dark'
      )
    const observer = new MutationObserver(read)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-bs-theme'],
    })
    read()
    return () => observer.disconnect()
  }, [])

  // ── Carga inicial de datos ──────────────────────────────────────────────────
  useEffect(() => {
    if (!supabase) return

    let active = true
    const fetchAll = async () => {
      setLoading(true)
      try {
        const [t, emp] = await Promise.all([
          supabase
            .schema('Tickets')
            .from('ticket')
            .select(
              'id,title,createdAt,systemOrigin,statusId,completed,responsibleId,priorityId,dueAt,firstResponseAt,resolvedAt'
            )
            .eq('deleted', false)
            .order('createdAt', { ascending: false }),
          employeeService.getAll(),
        ])

        if (!active) return
        setTickets(t.data || [])
        setEmployees(emp.success ? emp.data || [] : [])
      } catch (err) {
        console.error('Error cargando datos del reporte:', err)
        sileo.error({ title: 'Error al cargar datos' })
      } finally {
        if (active) setLoading(false)
      }
    }

    fetchAll()
    return () => {
      active = false
    }
  }, [supabase])

  // Mapa { empleadoId: "Nombre Apellido" }
  const employeeMap = useMemo(
    () =>
      Object.fromEntries(
        employees.map((e) => [
          e.id,
          `${e.nombre || ''} ${e.apellido || ''}`.trim() || `#${e.id}`,
        ])
      ),
    [employees]
  )

  const nameOf = (responsibleId) =>
    responsibleId
      ? employeeMap[responsibleId] || `#${responsibleId}`
      : 'Sin asignar'

  const origins = useMemo(
    () =>
      [...new Set(tickets.map((t) => t.systemOrigin).filter(Boolean))].sort(),
    [tickets]
  )

  // Solo empleados que tienen al menos un ticket asignado (para el filtro)
  const responsibles = useMemo(() => {
    const ids = [
      ...new Set(tickets.map((t) => t.responsibleId).filter(Boolean)),
    ]
    return ids
      .map((id) => ({ id, name: nameOf(id) }))
      .sort((a, b) => a.name.localeCompare(b.name))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, employeeMap])

  // ── Aplicación de filtros + enriquecimiento ──────────────────────────────────
  const filtered = useMemo(() => {
    return tickets
      .filter((t) => {
        const created = new Date(t.createdAt)
        if (from && created < new Date(from)) return false
        if (to) {
          const end = new Date(to)
          end.setHours(23, 59, 59, 999)
          if (created > end) return false
        }
        if (origin !== 'all' && t.systemOrigin !== origin) return false
        if (situacion !== 'all' && getSituacion(t) !== situacion) return false
        if (personId === 'unassigned' && t.responsibleId) return false
        if (
          personId !== 'all' &&
          personId !== 'unassigned' &&
          Number(t.responsibleId) !== Number(personId)
        )
          return false
        return true
      })
      .map((t) => ({
        ...t,
        situacion: getSituacion(t),
        responsableName: nameOf(t.responsibleId),
        slaStatus: getSlaInfo(t).resolution.status,
      }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, from, to, origin, situacion, personId, employeeMap])

  // ── KPIs derivados ───────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const total = filtered.length
    const pendientes = filtered.filter(
      (t) => t.situacion === 'Pendiente'
    ).length
    const enCurso = filtered.filter((t) => t.situacion === 'En curso').length
    const atendidos = filtered.filter((t) => t.situacion === 'Atendido').length

    // SLA: cumplimiento sobre los tickets ya "decididos" (cumplidos + incumplidos)
    const slaMet = filtered.filter((t) => t.slaStatus === 'met').length
    const slaBreached = filtered.filter(
      (t) => t.slaStatus === 'breached'
    ).length
    const slaAtRisk = filtered.filter((t) => t.slaStatus === 'at_risk').length
    const decided = slaMet + slaBreached
    const slaCompliance = decided ? Math.round((slaMet / decided) * 100) : null

    return {
      total,
      pendientes,
      enCurso,
      atendidos,
      slaMet,
      slaBreached,
      slaAtRisk,
      slaCompliance,
    }
  }, [filtered])

  // ── Resumen por persona ──────────────────────────────────────────────────────
  const byPerson = useMemo(() => {
    const map = new Map()
    for (const t of filtered) {
      const key = t.responsableName
      if (!map.has(key)) {
        map.set(key, {
          name: key,
          pendientes: 0,
          enCurso: 0,
          atendidos: 0,
          total: 0,
          slaMet: 0,
          slaBreached: 0,
        })
      }
      const row = map.get(key)
      if (t.situacion === 'Pendiente') row.pendientes++
      else if (t.situacion === 'En curso') row.enCurso++
      else if (t.situacion === 'Atendido') row.atendidos++
      if (t.slaStatus === 'met') row.slaMet++
      else if (t.slaStatus === 'breached') row.slaBreached++
      row.total++
    }
    // Calcular cumplimiento por persona y ordenar por carga
    return [...map.values()]
      .map((row) => {
        const decided = row.slaMet + row.slaBreached
        return {
          ...row,
          slaCompliance: decided
            ? Math.round((row.slaMet / decided) * 100)
            : null,
        }
      })
      .sort((a, b) => b.total - a.total)
  }, [filtered])

  // Máximos por columna para escalar la intensidad del heatmap
  const maxByCol = useMemo(
    () => ({
      pendientes: Math.max(1, ...byPerson.map((p) => p.pendientes)),
      enCurso: Math.max(1, ...byPerson.map((p) => p.enCurso)),
      atendidos: Math.max(1, ...byPerson.map((p) => p.atendidos)),
      total: Math.max(1, ...byPerson.map((p) => p.total)),
      slaBreached: Math.max(1, ...byPerson.map((p) => p.slaBreached)),
    }),
    [byPerson]
  )

  // ── Gráfico: barras apiladas por persona ─────────────────────────────────────
  useEffect(() => {
    if (loading || !barRef.current) return

    const fontColor = isDark ? 'rgba(255,255,255,0.6)' : '#64748b'
    const gridColor = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'

    // Limitamos a las 12 personas con más carga para mantener legibilidad
    const top = byPerson.slice(0, 12)

    const chart = new Chart(barRef.current, {
      type: 'bar',
      data: {
        labels: top.map((p) => p.name),
        datasets: [
          {
            label: 'Pendiente',
            data: top.map((p) => p.pendientes),
            backgroundColor: SIT_COLORS.Pendiente,
            borderRadius: 4,
            stack: 's',
          },
          {
            label: 'En curso',
            data: top.map((p) => p.enCurso),
            backgroundColor: SIT_COLORS['En curso'],
            borderRadius: 4,
            stack: 's',
          },
          {
            label: 'Atendido',
            data: top.map((p) => p.atendidos),
            backgroundColor: SIT_COLORS.Atendido,
            borderRadius: 4,
            stack: 's',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              color: fontColor,
              font: { size: 11 },
              boxWidth: 12,
              boxHeight: 12,
              usePointStyle: true,
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            ticks: { color: fontColor, precision: 0 },
            grid: { color: gridColor },
            beginAtZero: true,
          },
          y: {
            stacked: true,
            ticks: { color: fontColor },
            grid: { display: false },
          },
        },
      },
    })

    return () => chart.destroy()
  }, [loading, isDark, byPerson])

  // ── Gráfico: dona por situación ──────────────────────────────────────────────
  useEffect(() => {
    if (loading || !doughnutRef.current) return

    const fontColor = isDark ? 'rgba(255,255,255,0.6)' : '#64748b'

    const chart = new Chart(doughnutRef.current, {
      type: 'doughnut',
      data: {
        labels: ['Pendiente', 'En curso', 'Atendido'],
        datasets: [
          {
            data: [kpis.pendientes, kpis.enCurso, kpis.atendidos],
            backgroundColor: [
              SIT_COLORS.Pendiente,
              SIT_COLORS['En curso'],
              SIT_COLORS.Atendido,
            ],
            borderColor: isDark ? '#131c2e' : '#ffffff',
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '70%',
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              color: fontColor,
              font: { size: 11 },
              boxWidth: 12,
              boxHeight: 12,
              usePointStyle: true,
            },
          },
        },
      },
    })

    return () => chart.destroy()
  }, [loading, isDark, kpis])

  // ── Metadatos para el reporte ────────────────────────────────────────────────
  const reportMeta = useMemo(() => {
    const rangeLabel =
      from || to
        ? `Periodo: ${from ? new Date(from).toLocaleDateString('es-CR') : 'Inicio'} — ${
            to ? new Date(to).toLocaleDateString('es-CR') : 'Hoy'
          }`
        : 'Periodo: Histórico completo'

    let personLabel = 'Todos'
    if (personId === 'unassigned') personLabel = 'Sin asignar'
    else if (personId !== 'all') personLabel = nameOf(personId)

    return {
      rangeLabel,
      generatedAt: new Date().toLocaleString('es-CR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      originLabel: origin === 'all' ? 'Todos' : origin,
      situationLabel: situacion === 'all' ? 'Todas' : situacion,
      personLabel,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, origin, situacion, personId, employeeMap])

  // ── Exportaciones ────────────────────────────────────────────────────────────
  const handleExportPDF = async () => {
    if (!filtered.length) {
      sileo.warning({ title: 'No hay tickets para exportar' })
      return
    }
    try {
      setExporting(true)
      await generateReportPDF(filtered, { byPerson, meta: reportMeta })
      sileo.success({ title: 'Reporte PDF generado' })
    } catch (err) {
      console.error('Error generando PDF:', err)
      sileo.error({ title: 'No se pudo generar el PDF' })
    } finally {
      setExporting(false)
    }
  }

  const handleExportCSV = () => {
    if (!filtered.length) {
      sileo.warning({ title: 'No hay tickets para exportar' })
      return
    }
    const headers = [
      'ID',
      'Título',
      'Origen',
      'Responsable',
      'Situación',
      'SLA',
      'Creado',
    ]
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
    const rows = filtered.map((t) =>
      [
        t.id,
        t.title,
        t.systemOrigin || '',
        t.responsableName,
        t.situacion,
        (SLA_META[t.slaStatus] || {}).label || '',
        new Date(t.createdAt).toLocaleDateString('es-CR'),
      ]
        .map(escape)
        .join(',')
    )
    const csv = [headers.map(escape).join(','), ...rows].join('\n')
    // BOM para que Excel reconozca UTF-8
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `reporte-tickets-persona-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
    sileo.success({ title: 'Reporte CSV generado' })
  }

  const handleReset = () => {
    setFrom('')
    setTo('')
    setOrigin('all')
    setSituacion('all')
    setPersonId('all')
  }

  const kpiCards = [
    { label: 'Total', value: kpis.total, accent: 'var(--gm-primary)' },
    { label: 'Pendientes', value: kpis.pendientes, accent: 'var(--accent)' },
    { label: 'En Curso', value: kpis.enCurso, accent: 'var(--brand)' },
    { label: 'Atendidos', value: kpis.atendidos, accent: 'var(--gm-success)' },
    {
      label: 'SLA Cumplido',
      value: kpis.slaCompliance === null ? '—' : `${kpis.slaCompliance}%`,
      accent: 'var(--gm-success)',
    },
    { label: 'SLA Incumplido', value: kpis.slaBreached, accent: 'var(--gm-danger)' },
  ]

  return (
    <AuthAccessValidator AccessNames={['ticket_support']} showError={true}>
      <div className="reports-page">
        <HeaderSection
          compact
          title="Reportes"
          subtitle="Tickets pendientes, en curso y atendidos por persona"
          icon="fa-solid fa-file-lines"
        />

        {/* FILTROS + EXPORTACIONES (una sola barra) */}
        <div className="rp-bar mb-3">
          <div className="rp-field">
            <label htmlFor="rp-from">Desde</label>
            <input
              id="rp-from"
              type="date"
              value={from}
              max={to || undefined}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div className="rp-field">
            <label htmlFor="rp-to">Hasta</label>
            <input
              id="rp-to"
              type="date"
              value={to}
              min={from || undefined}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div className="rp-field">
            <label htmlFor="rp-origin">Plataforma</label>
            <select
              id="rp-origin"
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
            >
              <option value="all">Todas</option>
              {origins.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
          <div className="rp-field">
            <label htmlFor="rp-sit">Situación</label>
            <select
              id="rp-sit"
              value={situacion}
              onChange={(e) => setSituacion(e.target.value)}
            >
              <option value="all">Todas</option>
              <option value="Pendiente">Pendientes</option>
              <option value="En curso">En curso</option>
              <option value="Atendido">Atendidos</option>
            </select>
          </div>
          <div className="rp-field is-wide">
            <label htmlFor="rp-person">Responsable</label>
            <select
              id="rp-person"
              value={personId}
              onChange={(e) => setPersonId(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="unassigned">Sin asignar</option>
              {responsibles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>

          <div className="rp-actions">
            <button
              type="button"
              className="rp-icon-btn"
              onClick={handleReset}
              disabled={loading}
              title="Limpiar filtros"
              aria-label="Limpiar filtros"
            >
              <i className="fa-solid fa-rotate-left" />
            </button>
            <button
              type="button"
              className="rp-btn"
              onClick={handleExportCSV}
              disabled={loading}
              title="Exportar CSV"
            >
              <i className="fa-solid fa-file-csv" />
              <span>CSV</span>
            </button>
            <button
              type="button"
              className="rp-btn is-primary"
              onClick={handleExportPDF}
              disabled={exporting || loading}
              title="Exportar PDF"
            >
              <i
                className={`fa-solid ${exporting ? 'fa-spinner fa-spin' : 'fa-file-pdf'}`}
              />
              <span>PDF</span>
            </button>
          </div>
        </div>

        {/* KPIs en una sola tira */}
        <div className="rp-kpis mb-3">
          {kpiCards.map((k) => (
            <div key={k.label} className="rp-kpi">
              <span className="rp-kpi-dot" style={{ background: k.accent }} />
              <span className="rp-kpi-value">{k.value}</span>
              <span className="rp-kpi-label">{k.label}</span>
            </div>
          ))}
          {loading && (
            <span className="rp-kpi-loading">
              <span className="spinner-border spinner-border-sm text-primary" />
            </span>
          )}
        </div>

        <div className="rp-grid">
          {/* RESUMEN POR PERSONA */}
          <section className="tt rp-table-card" style={{ flex: 'none' }}>
            <div className="tt-bar">
              <span className="rp-title">Resumen por persona</span>
              <span className="tt-muted">
                {byPerson.length} responsables
                <span className="rp-heat-legend d-none d-sm-inline-flex">
                  menos <span className="rp-heat-bar" /> más
                </span>
              </span>
            </div>
            <div className="tt-scroll">
              <table className="tt-table rp-table">
                <thead>
                  <tr>
                    <th>Responsable</th>
                    <th className="text-center">Pend.</th>
                    <th className="text-center">En curso</th>
                    <th className="text-center">Atend.</th>
                    <th className="text-center">Total</th>
                    <th className="text-center">Incumpl.</th>
                    <th className="text-center">% SLA</th>
                  </tr>
                </thead>
                <tbody>
                  {byPerson.length === 0 && !loading ? (
                    <tr>
                      <td colSpan={7} className="tt-empty">
                        No hay datos para los filtros seleccionados.
                      </td>
                    </tr>
                  ) : (
                    byPerson.map((p) => (
                      <tr key={p.name}>
                        <td className="rp-name">{p.name}</td>
                        {[
                          ['pendientes', HEAT_RGB.pendientes],
                          ['enCurso', HEAT_RGB.enCurso],
                          ['atendidos', HEAT_RGB.atendidos],
                          ['total', HEAT_RGB.total],
                          ['slaBreached', HEAT_RGB.breached],
                        ].map(([key, rgb]) => (
                          <td key={key} className="text-center">
                            <span
                              className="rp-heat"
                              style={heatStyle(p[key], maxByCol[key], rgb, isDark)}
                            >
                              {p[key]}
                            </span>
                          </td>
                        ))}
                        <td
                          className="text-center rp-pct"
                          style={{ color: slaPctColor(p.slaCompliance) }}
                        >
                          {p.slaCompliance === null
                            ? '—'
                            : `${p.slaCompliance}%`}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                {byPerson.length > 0 && (
                  <tfoot>
                    <tr className="rp-total">
                      <td>Total</td>
                      <td className="text-center">{kpis.pendientes}</td>
                      <td className="text-center">{kpis.enCurso}</td>
                      <td className="text-center">{kpis.atendidos}</td>
                      <td className="text-center">{kpis.total}</td>
                      <td className="text-center">{kpis.slaBreached}</td>
                      <td
                        className="text-center"
                        style={{ color: slaPctColor(kpis.slaCompliance) }}
                      >
                        {kpis.slaCompliance === null
                          ? '—'
                          : `${kpis.slaCompliance}%`}
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </section>

          {/* GRÁFICOS */}
          <div className="rp-charts">
            <section className="rp-card">
              <span className="rp-title">Distribución por situación</span>
              {kpis.total === 0 && !loading ? (
                <div className="rp-empty">Sin tickets.</div>
              ) : (
                <div style={{ height: 190 }}>
                  <canvas ref={doughnutRef} />
                </div>
              )}
            </section>

            <section className="rp-card">
              <span className="rp-title">
                Carga por persona
                {byPerson.length > 12 && (
                  <span className="tt-muted"> · top 12</span>
                )}
              </span>
              {byPerson.length === 0 && !loading ? (
                <div className="rp-empty">Sin datos.</div>
              ) : (
                <div
                  style={{
                    height: Math.max(
                      150,
                      byPerson.slice(0, 12).length * 30 + 50
                    ),
                  }}
                >
                  <canvas ref={barRef} />
                </div>
              )}
            </section>
          </div>
        </div>

        <style>{`
          /* Barra de filtros y exportaciones */
          .rp-bar {
            display: flex; flex-wrap: wrap; align-items: flex-end; gap: 8px;
            padding: 8px 10px; border: 1px solid var(--gm-border); border-radius: 12px;
            background: var(--gm-surface);
          }
          .rp-field { display: flex; flex-direction: column; gap: 2px; flex: 1 1 120px; min-width: 110px; }
          .rp-field.is-wide { flex-basis: 170px; }
          .rp-field label {
            padding-left: 2px; font-size: 0.62rem; font-weight: 700; letter-spacing: 0.05em;
            text-transform: uppercase; color: var(--gm-text-faint);
          }
          .rp-field input, .rp-field select {
            width: 100%; height: 32px; padding: 0 8px; border-radius: 8px;
            border: 1px solid var(--gm-border); background: var(--gm-surface-2);
            color: var(--gm-text); font-size: 0.8rem;
          }
          .rp-field input:focus, .rp-field select:focus { outline: none; border-color: var(--gm-primary); }
          [data-bs-theme='dark'] .rp-field input { color-scheme: dark; }
          .rp-actions { display: flex; gap: 6px; margin-left: auto; }
          .rp-btn, .rp-icon-btn {
            display: inline-flex; align-items: center; justify-content: center; gap: 6px; height: 32px;
            border-radius: 8px; border: 1px solid var(--gm-border); background: transparent;
            color: var(--gm-text-muted); font-size: 0.8rem; font-weight: 500;
          }
          .rp-btn { padding: 0 12px; }
          .rp-icon-btn { width: 32px; border-color: transparent; }
          .rp-btn:hover, .rp-icon-btn:hover { background: var(--gm-surface-2); color: var(--gm-text); }
          .rp-btn.is-primary { background: var(--gm-primary); border-color: var(--gm-primary); color: #fff; }
          .rp-btn.is-primary:hover { background: var(--gm-primary-hover); }
          .rp-btn:disabled, .rp-icon-btn:disabled { opacity: 0.55; }

          /* KPIs en una tira */
          .rp-kpis {
            display: flex; flex-wrap: wrap; align-items: center;
            border: 1px solid var(--gm-border); border-radius: 12px; background: var(--gm-surface);
          }
          .rp-kpi { display: flex; align-items: center; gap: 7px; padding: 8px 16px; flex: 1 1 auto; }
          .rp-kpi + .rp-kpi { border-left: 1px solid var(--gm-border); }
          .rp-kpi-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
          .rp-kpi-value { font-size: 1.05rem; font-weight: 700; color: var(--gm-text); font-variant-numeric: tabular-nums; }
          .rp-kpi-label { font-size: 0.75rem; color: var(--gm-text-muted); white-space: nowrap; }
          .rp-kpi-loading { padding: 0 12px; }

          /* Contenido: tabla + gráficos */
          .rp-grid { display: grid; grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr); gap: 12px; align-items: start; }
          @media (max-width: 1199px) { .rp-grid { grid-template-columns: 1fr; } }
          .rp-title { font-size: 0.72rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--gm-text-muted); }
          .rp-table td { height: 34px; }
          .rp-name { font-weight: 600; max-width: 240px; overflow: hidden; text-overflow: ellipsis; }
          .rp-heat {
            display: inline-flex; align-items: center; justify-content: center; min-width: 30px;
            padding: 2px 8px; border-radius: 6px; font-size: 0.78rem; font-weight: 600;
            font-variant-numeric: tabular-nums;
          }
          .rp-pct { font-weight: 700; font-variant-numeric: tabular-nums; }
          .rp-total td {
            height: 34px; padding: 6px 10px; font-weight: 700; color: var(--gm-text);
            border-top: 1px solid var(--gm-border); background: var(--gm-surface-2);
          }
          .rp-heat-legend { align-items: center; gap: 6px; margin-left: 12px; font-size: 0.68rem; }
          .rp-heat-bar {
            width: 60px; height: 6px; border-radius: 99px;
            background: linear-gradient(90deg, rgba(99, 102, 241, 0.12), rgba(99, 102, 241, 0.85));
          }
          .rp-charts { display: flex; flex-direction: column; gap: 12px; }
          .rp-card {
            display: flex; flex-direction: column; gap: 8px; padding: 12px 14px;
            border: 1px solid var(--gm-border); border-radius: 12px; background: var(--gm-surface);
          }
          .rp-empty { padding: 32px 8px; text-align: center; font-size: 0.82rem; color: var(--gm-text-faint); }
        `}</style>
      </div>
    </AuthAccessValidator>
  )
}

export default Reports
